import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useMsal } from "@azure/msal-react";
import { useMsalAuth } from "../hooks/useMsalAuth";
import { errorHandler } from "./shared/errorhandler";
import { ROUTES } from "./shared/constant";
import SkypointLogoLoader from "./SkypointLogoLander/SkypointLogoLoader";
import { NoAccessCard } from "./NoAccessCard";
import { authService } from "../utils/authService";
import { loadUserPreferences } from "../services/userPreferencesService";

function Callback(): React.ReactElement {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();
  const { inProgress } = useMsal();
  const { currentUser, getAuthorizationHeader } = useMsalAuth();
  const hasAttemptedLogin = useRef(false);

  useEffect(() => {
    if (inProgress === "startup" || inProgress === "handleRedirect") {
      console.log("[Callback] MSAL is processing redirect, waiting...", { inProgress });
      return;
    }

    if (hasAttemptedLogin.current) {
      return;
    }

    const user = currentUser;
    console.log("[Callback] Checking user after MSAL ready", {
      user: user ? { id: user.id, name: user.name } : null,
      inProgress,
    });

    if (!user) {
      console.error("[Callback] No user found after MSAL processing");
      setErrorMessage("Authentication failed - no user found");
      setIsError(true);
      return;
    }

    hasAttemptedLogin.current = true;
    const userExistsRequest = { objectId: user.id };
    // Phase 1: Call our local server directly (no Studio gateway)
    const apiUrl = "/api/user/login-studio";

    console.log("[Callback] Calling login-studio API", {
      apiUrl,
      origin: window.location.origin,
      userId: user.id,
    });

    fetch(apiUrl, {
      method: "POST",
      headers: getAuthorizationHeader(),
      body: JSON.stringify(userExistsRequest),
    })
      .then(errorHandler)
      .then((response) => response.json())
      .then(async (data) => {
        console.log("[Callback] login-studio response", {
          isSuccess: data.isSuccess,
          instanceId: data.instanceId,
        });

        if (data.isSuccess) {
          // Load user preferences from DB (fresh login - localStorage is empty)
          const preferences = await loadUserPreferences();

          // Priority: DB preferences > localStorage > API default
          const instanceId =
            preferences?.instanceId || authService.getSavedInstanceId() || data.instanceId;

          // Redirect to the page the user originally intended to visit (e.g. /scheduled-tours
          // from an alert email link). The path was preserved across the auth round-trip via
          // LoginHandler → authService.login (OAuth `state` + sessionStorage);
          // msalConfig.handleRedirectPromise restores it into sessionStorage so we read from
          // one place here.
          const savedRedirect = sessionStorage.getItem("redirectAfterLogin");
          sessionStorage.removeItem("redirectAfterLogin");
          if (savedRedirect) {
            // Parse via URL so we can do a case-insensitive check for an existing
            // instanceid param (alert emails use lowercase, but defensive against camelCase).
            const url = new URL(savedRedirect, window.location.origin);
            const hasInstanceId = Array.from(url.searchParams.keys()).some(
              (k) => k.toLowerCase() === "instanceid",
            );
            if (!hasInstanceId) {
              url.searchParams.set("instanceid", instanceId);
            }
            navigate(url.pathname + url.search, { replace: true });
          } else {
            navigate(`${ROUTES.HOME}?instanceid=${instanceId}`, { replace: true });
          }
        } else {
          console.error("[Callback] login-studio returned failure", data);
          setErrorMessage(data.message || "Login failed");
          setIsError(true);
        }
      })
      .catch((error) => {
        console.error("[Callback] login-studio API error", error);
        setErrorMessage(error.message || "Failed to connect to server");
        setIsError(true);
      });
  }, [navigate, currentUser, getAuthorizationHeader, inProgress]);

  if (isError) {
    console.log("[Callback] Showing error card", { errorMessage });
    return <NoAccessCard />;
  }

  return <SkypointLogoLoader />;
}

export default Callback;
